import { useState, useEffect, ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '@utils/userHelper';
import type { User } from '../types/models/user';

export interface WithUserProps {
  user: User;
  fetchUserData: () => Promise<void>;
}

const withUser = <P extends WithUserProps>(WrappedComponent: ComponentType<P>) => {
  const WithUserComponent = (props: Omit<P, keyof WithUserProps>) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const navigate = useNavigate();

    const clearGodModeSession = (): void => {
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonated_user');
      sessionStorage.removeItem('godmode_original_user');
      sessionStorage.removeItem('godmode_original_token');
    };

    const isAuthFailure = (err: unknown): boolean => {
      // Only treat HTTP 401/403 as a true auth failure. Network errors,
      // timeouts, and 5xx must NOT wipe the session — that was forcing
      // owners out on every network blip.
      const ax = err as { response?: { status?: number } } | null;
      const status = ax?.response?.status;
      return status === 401 || status === 403;
    };

    const fetchUserData = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const userData = await Promise.race([
          getUser(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), 15000)
          )
        ]);
        setUser(userData);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err instanceof Error ? err : new Error(String(err)));

        if (isAuthFailure(err)) {
          // Real 401/403: server says auth is invalid. Clean up and route to login.
          clearGodModeSession();
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.setItem('intendedPath', window.location.pathname);
          navigate('/');
        }
        // Otherwise: the failure was transient (timeout, network). Keep the
        // session, just show a retryable error so the user doesn't get
        // kicked out by a flaky connection.
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchUserData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
      return <div>Loading...</div>;
    }

    if (error && !user) {
      // Transient failure but no cached user. Stay on the page with a retry,
      // not a forced reload.
      return (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <p>Couldn’t load your profile.</p>
          <button type="button" onClick={() => { setError(null); setLoading(true); fetchUserData(); }}>
            Retry
          </button>
        </div>
      );
    }

    const allProps = { ...props, user: user as User, fetchUserData } as P;
    return <WrappedComponent {...allProps} />;
  };

  WithUserComponent.displayName = `WithUser(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithUserComponent;
};

export default withUser;
